import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TracePreviewBaseDto } from '@terra/shared-contracts-trace';
import { TraceStatusSchema } from '@terra/shared-schemas-ai';
import { terraProxyApiClient } from '../clients/testerClient';

const SIGNAL_TRACES_QUERY_KEY = 'signal-traces';
const SIGNAL_TRACES_REFETCH_INTERVAL_MS = 3000;
const SIGNAL_TRACES_EMPTY_REFETCH_WINDOW_MS = 30000;
const SIGNAL_TRACES_STALE_TIME_MS = 0;

const hasRunningTrace = (traces: Pick<TracePreviewBaseDto, 'status'>[]) =>
  traces.some((trace) => trace.status === TraceStatusSchema.enum.RUNNING);

export const getSignalTracesRefetchInterval = ({
  traces,
  dataUpdatedAt,
  emptyPollingStartedAt,
  now,
}: {
  traces: Pick<TracePreviewBaseDto, 'status'>[];
  dataUpdatedAt: number;
  emptyPollingStartedAt: number | null;
  now: number;
}): {
  refetchInterval: number | false;
  emptyPollingStartedAt: number | null;
} => {
  if (dataUpdatedAt === 0) {
    return {
      refetchInterval: SIGNAL_TRACES_REFETCH_INTERVAL_MS,
      emptyPollingStartedAt,
    };
  }

  if (traces.length === 0) {
    const nextEmptyPollingStartedAt = emptyPollingStartedAt ?? dataUpdatedAt;
    return {
      refetchInterval:
        now - nextEmptyPollingStartedAt < SIGNAL_TRACES_EMPTY_REFETCH_WINDOW_MS
          ? SIGNAL_TRACES_REFETCH_INTERVAL_MS
          : false,
      emptyPollingStartedAt: nextEmptyPollingStartedAt,
    };
  }

  return {
    refetchInterval: hasRunningTrace(traces)
      ? SIGNAL_TRACES_REFETCH_INTERVAL_MS
      : false,
    emptyPollingStartedAt: null,
  };
};

export function useTracesBySignalId({
  signalId,
  enabled = true,
}: {
  signalId: string | undefined;
  enabled?: boolean;
}) {
  const emptyPollingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    emptyPollingStartedAtRef.current = null;
  }, [signalId, enabled]);

  return useQuery({
    queryKey: [SIGNAL_TRACES_QUERY_KEY, signalId],
    queryFn: async () => {
      if (!signalId) {
        return [];
      }
      return terraProxyApiClient.getTracesBySignalId(signalId);
    },
    enabled: enabled && !!signalId,
    staleTime: SIGNAL_TRACES_STALE_TIME_MS,
    refetchInterval: (queryResult) => {
      const result = getSignalTracesRefetchInterval({
        traces: queryResult.state.data ?? [],
        dataUpdatedAt: queryResult.state.dataUpdatedAt,
        emptyPollingStartedAt: emptyPollingStartedAtRef.current,
        now: Date.now(),
      });

      emptyPollingStartedAtRef.current = result.emptyPollingStartedAt;

      return result.refetchInterval;
    },
  });
}
